import LoadingButton from '@mui/lab/LoadingButton';
import {
    alpha,
    Box,
    Button,
    Checkbox,
    DialogContent,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Typography,
} from '@mui/material';
import moment from 'moment';
import { useDialog } from 'muibox';
import { useSnackbar } from 'notistack';
import { useMemo, useState } from 'react';
import { OnlyFansMassDMMessageScheduleDocumentWithColor } from '../../../../hooks/useMessageUsersMassDmList';
import DialogAdaptive from '../../../common/DialogAdaptive';
import StyledCardTitleBar from '../../../common/StyledCardTitleBar';
import StyledDialogActions from '../../../common/StyledDialogActions';

type Props = {
    active: boolean;
    scheduledMessages: OnlyFansMassDMMessageScheduleDocumentWithColor[];
    refetchMassDmsSchedule: () => void;
    bulkDeleteScheduledMessages: (scheduleIds: string[], active: boolean) => Promise<{ success: boolean }>;
    bulkDeleteScheduledMessagesLoading: boolean;
    open: boolean;
    setOpen: (open: boolean) => void;
};

type SortField = 'name' | 'active' | 'scheduleDate' | 'price';
type SortDirection = 'asc' | 'desc';

const MessageUsersAutoMassDmBulkDeleteDialog = (props: Props) => {
    const {
        active,
        scheduledMessages,
        refetchMassDmsSchedule,
        bulkDeleteScheduledMessages,
        bulkDeleteScheduledMessagesLoading,
        open,
        setOpen,
    } = props;
    const { enqueueSnackbar } = useSnackbar();
    const dialog = useDialog();

    const [selectedScheduleIds, setSelectedScheduleIds] = useState<string[]>([]);
    const [selectedScheduledMessages, setSelectedScheduledMessages] = useState<OnlyFansMassDMMessageScheduleDocumentWithColor[]>([]);

    // Sorting state
    const [sortField, setSortField] = useState<SortField>('name');
    const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

    // Sort the messages based on current sort settings
    const sortedMessages = useMemo(() => {
        return [...scheduledMessages].sort((a, b) => {
            let aValue: any;
            let bValue: any;

            switch (sortField) {
                case 'name':
                    aValue = a.name.toLowerCase();
                    bValue = b.name.toLowerCase();
                    break;
                case 'active':
                    aValue = a.active;
                    bValue = b.active;
                    break;
                case 'scheduleDate':
                    aValue = a.scheduleDate ? new Date(a.scheduleDate).getTime() : 0;
                    bValue = b.scheduleDate ? new Date(b.scheduleDate).getTime() : 0;
                    break;
                case 'price':
                    aValue = a.price ?? 0;
                    bValue = b.price ?? 0;
                    break;
                default:
                    return 0;
            }

            if (aValue < bValue) {
                return sortDirection === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
                return sortDirection === 'asc' ? 1 : -1;
            }
            return 0;
        });
    }, [scheduledMessages, sortField, sortDirection]);

    const handleSort = (field: SortField) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const handleClose = () => {
        // Reset selected scheduled messages when the dialog is closed
        setSelectedScheduledMessages([]);
        setSelectedScheduleIds([]);
        // Close the dialog
        setOpen(false);
    };

    // Handle delete action
    const handleDelete = () => {
        dialog
            .confirm({
                title: 'Delete Scheduled Mass DMs',
                message: `Are you sure you want to delete ${selectedScheduledMessages.length.toLocaleString()} Scheduled Mass DMs?`,
                ok: {
                    text: 'Delete',
                    color: 'error',
                    variant: 'contained',
                },
                cancel: {
                    text: 'Cancel',
                    color: 'secondary',
                    variant: 'text',
                },
            })
            .then(() => {
                // Perform delete action
                bulkDeleteScheduledMessages(selectedScheduleIds, active)
                    .then(res => {
                        if (res.success) {
                            enqueueSnackbar(
                                active ? 'Scheduled Mass DMs scheduled for deleting' : 'Scheduled Mass DMs deleted successfully',
                                {
                                    variant: 'success',
                                },
                            );

                            refetchMassDmsSchedule();
                            handleClose();
                        } else {
                            enqueueSnackbar('Failed to delete Scheduled Mass DMs', { variant: 'error' });
                        }
                    })
                    .catch(err => {
                        console.error(err);
                        enqueueSnackbar('Failed to delete Scheduled Mass DMs', { variant: 'error' });
                    });
            })
            .catch(() => {
                // Handle cancel
            });
    };

    return (
        <DialogAdaptive open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
            <StyledCardTitleBar title={`Scheduled Mass DMs Bulk Delete (${active ? 'Active' : 'Inactive'})`} />
            <DialogContent sx={{ pl: 0, pr: 0 }}>
                <Stack direction="column" spacing={2}>
                    <Typography variant="body1" color="text.secondary" pl={2} pr={2}>
                        Select the Scheduled Mass DMs you want to delete. You can select all Scheduled Mass DMs by checking the checkbox in
                        the header.
                    </Typography>
                    <Typography variant="body2" color="text.secondary" pl={2} pr={2}>
                        {selectedScheduledMessages.length.toLocaleString()} Scheduled Mass DMs selected
                    </Typography>

                    <Box
                        sx={{
                            width: '100%',
                            overflow: 'hidden',
                            backgroundColor: theme => alpha(theme.palette.error.main, 0.1),
                        }}
                    >
                        <TableContainer
                            sx={{
                                maxHeight: 400,
                                // Reduce the padding between the cells
                                '& .MuiTableCell-root': {
                                    padding: '4px 8px',
                                },
                                // remove the left padding of the first cell
                                '& .MuiTableCell-root:first-of-type': {
                                    paddingLeft: 0,
                                },
                                // remove the right padding of the last cell
                                '& .MuiTableCell-root:last-of-type': {
                                    paddingRight: 2,
                                },
                            }}
                        >
                            <Table
                                size="small"
                                sx={{
                                    // Pin the header row
                                    '& thead th': {
                                        position: 'sticky',
                                        top: 0,
                                        backgroundColor: 'rgb(239, 210, 208)', // Error theme color
                                        zIndex: 1,
                                    },
                                    // Pin the first column
                                    '& th:first-of-type, & td:first-of-type': {
                                        position: 'sticky',
                                        left: 0,
                                        zIndex: 1,
                                    },
                                }}
                            >
                                <TableHead
                                    sx={{
                                        backgroundColor: 'rgb(239, 210, 208)', // Error theme color
                                        // pin the first column
                                        '& th:first-of-type': {
                                            position: 'sticky',
                                            left: 0,
                                            zIndex: 2,
                                        },
                                    }}
                                >
                                    <TableRow
                                        sx={{
                                            // every cell in the header row
                                            '& th': {
                                                backgroundColor: theme => theme.palette.background.paper,
                                                color: theme => theme.palette.text.primary,
                                                fontWeight: 'bold',
                                            },
                                        }}
                                    >
                                        <TableCell width={50} align="center">
                                            <Checkbox
                                                indeterminate={
                                                    selectedScheduleIds.length > 0 && selectedScheduleIds.length < sortedMessages.length
                                                }
                                                checked={selectedScheduledMessages.length === sortedMessages.length}
                                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                    if (e.target.checked) {
                                                        setSelectedScheduleIds(sortedMessages.map(message => message._id));
                                                        setSelectedScheduledMessages(sortedMessages);
                                                    } else {
                                                        setSelectedScheduleIds([]);
                                                        setSelectedScheduledMessages([]);
                                                    }
                                                }}
                                                color="error"
                                                size="small"
                                                sx={{
                                                    cursor: 'pointer',
                                                    '&:hover': {
                                                        backgroundColor: 'transparent',
                                                    },
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <TableSortLabel
                                                active={sortField === 'name'}
                                                direction={sortField === 'name' ? sortDirection : 'asc'}
                                                onClick={() => handleSort('name')}
                                                sx={{
                                                    fontWeight: 'bold',
                                                    '& .MuiTableSortLabel-icon': {
                                                        color: 'inherit !important',
                                                    },
                                                }}
                                            >
                                                Mass DM Name
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell align="center" width={80}>
                                            <TableSortLabel
                                                active={sortField === 'active'}
                                                direction={sortField === 'active' ? sortDirection : 'asc'}
                                                onClick={() => handleSort('active')}
                                                sx={{
                                                    fontWeight: 'bold',
                                                    '& .MuiTableSortLabel-icon': {
                                                        color: 'inherit !important',
                                                    },
                                                }}
                                            >
                                                Active
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell width={150}>
                                            <TableSortLabel
                                                active={sortField === 'scheduleDate'}
                                                direction={sortField === 'scheduleDate' ? sortDirection : 'asc'}
                                                onClick={() => handleSort('scheduleDate')}
                                                sx={{
                                                    fontWeight: 'bold',
                                                    '& .MuiTableSortLabel-icon': {
                                                        color: 'inherit !important',
                                                    },
                                                }}
                                            >
                                                Scheduled Date
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell width={80} align="center">
                                            <TableSortLabel
                                                active={sortField === 'price'}
                                                direction={sortField === 'price' ? sortDirection : 'asc'}
                                                onClick={() => handleSort('price')}
                                                sx={{
                                                    fontWeight: 'bold',
                                                    '& .MuiTableSortLabel-icon': {
                                                        color: 'inherit !important',
                                                    },
                                                }}
                                            >
                                                Price
                                            </TableSortLabel>
                                        </TableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {sortedMessages.map(scheduledMessage => (
                                        <TableRow
                                            key={scheduledMessage._id}
                                            sx={{
                                                '&:last-child td, &:last-child th': {
                                                    border: 0,
                                                },
                                                backgroundColor: theme =>
                                                    selectedScheduleIds.includes(scheduledMessage._id)
                                                        ? alpha(theme.palette.error.main, 0.2)
                                                        : 'transparent',
                                            }}
                                        >
                                            <TableCell align="center">
                                                <Checkbox
                                                    checked={selectedScheduleIds.includes(scheduledMessage._id)}
                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                        if (e.target.checked) {
                                                            setSelectedScheduleIds([...selectedScheduleIds, scheduledMessage._id]);
                                                            setSelectedScheduledMessages([...selectedScheduledMessages, scheduledMessage]);
                                                        } else {
                                                            setSelectedScheduleIds(
                                                                selectedScheduleIds.filter(id => id !== scheduledMessage._id),
                                                            );
                                                            setSelectedScheduledMessages(
                                                                selectedScheduledMessages.filter(sm => sm._id !== scheduledMessage._id),
                                                            );
                                                        }
                                                    }}
                                                    color="error"
                                                    size="small"
                                                    sx={{
                                                        cursor: 'pointer',
                                                        '&:hover': {
                                                            backgroundColor: 'transparent',
                                                        },
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" color="text.primary" noWrap>
                                                    {scheduledMessage.name}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="center">
                                                <Typography
                                                    variant="body2"
                                                    color={scheduledMessage.active ? 'success.main' : 'text.secondary'}
                                                    noWrap
                                                >
                                                    {scheduledMessage.active ? 'Yes' : 'No'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" color="text.secondary" noWrap>
                                                    {scheduledMessage.scheduleDate
                                                        ? moment(scheduledMessage.scheduleDate).format('DD/MM/YYYY hh:mm A')
                                                        : 'Not scheduled'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="center">
                                                <Typography variant="body2" color="text.secondary" noWrap>
                                                    {(scheduledMessage.type || 'manual') === 'template'
                                                        ? 'From template'
                                                        : scheduledMessage.price
                                                        ? `$${scheduledMessage.price.toFixed(2)}`
                                                        : 'Free'}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                </Stack>
            </DialogContent>

            <StyledDialogActions>
                <Button onClick={handleClose} color="secondary" variant="text">
                    Cancel
                </Button>
                <LoadingButton
                    variant="contained"
                    color="error"
                    size="small"
                    loading={bulkDeleteScheduledMessagesLoading}
                    disabled={selectedScheduleIds.length === 0}
                    onClick={() => {
                        handleDelete();
                    }}
                >
                    <span>Delete Selected</span>
                </LoadingButton>
            </StyledDialogActions>
        </DialogAdaptive>
    );
};

export default MessageUsersAutoMassDmBulkDeleteDialog;
